import React from "react";
import {
  Activity,
  AlertTriangle,
  Cpu,
  Database,
  Settings,
  Layers,
  Radar,
  RefreshCcw,
  UploadCloud,
  PlayCircle,
  BrainCircuit,
  MapPinned,
} from "lucide-react";

export default function OpsConsolePage() {
  return (
    <div className="h-full w-full p-6 overflow-auto text-gray-200 bg-bg">
      
      {/* PAGE TITLE */}
      <div className="mb-6">
        <div className="text-xl font-semibold tracking-wide">Operations Console</div>
        <div className="text-xs text-fg-secondary font-mono uppercase tracking-[0.14em]">
          System Overwatch & AI Operations
        </div>
      </div>

      {/* SYSTEM OVERWATCH GRID */}
      <SectionTitle title="System Overwatch" />
      <div className="grid grid-cols-4 gap-4 mb-10">
        <MetricCard
          icon={<Activity size={20} className="text-status-busy" />}
          title="Pipelines"
          value="3 Running"
          sub="1 queued · 0 failed"
        />
        <MetricCard
          icon={<BrainCircuit size={20} className="text-status-busy" />}
          title="AI Engines"
          value="All Online"
          sub="5 active models"
        />
        <MetricCard
          icon={<Database size={20} className="text-yellow-400" />}
          title="Storage"
          value="Healthy"
          sub="112 TB used · 24 TB free"
        />
        <MetricCard
          icon={<AlertTriangle size={20} className="text-red-400" />}
          title="Alerts"
          value="2"
          sub="1 critical · 1 warning"
        />
      </div>

      {/* AI USE CASES */}
      <SectionTitle title="Active AI Products" />
      <div className="grid grid-cols-3 gap-4 mb-10">
        <UseCaseCard
          icon={<MapPinned size={18} />}
          title="Well Finder"
          status="Active"
          lastRun="22 min ago"
          alerts="0"
        />
        <UseCaseCard
          icon={<Layers size={18} />}
          title="Pad Reclamation"
          status="Training Soon"
          lastRun="—"
          alerts="—"
        />
        <UseCaseCard
          icon={<Cpu size={18} />}
          title="Tank Detection"
          status="Active"
          lastRun="3h ago"
          alerts="1"
        />
        <UseCaseCard
          icon={<Radar size={18} />}
          title="Change Detection"
          status="Idle"
          lastRun="Yesterday"
          alerts="0"
        />
        <UseCaseCard
          icon={<AlertTriangle size={18} />}
          title="Spill Perimeter"
          status="Active"
          lastRun="1h ago"
          alerts="0"
        />
        <UseCaseCard
          icon={<BrainCircuit size={18} />}
          title="Flowline Extraction"
          status="Model Update"
          lastRun="—"
          alerts="—"
        />
      </div>

      {/* OPERATIONAL QUEUES */}
      <SectionTitle title="Operational Queues" />
      <div className="grid grid-cols-2 gap-6 mb-12">
        
        {/* Ingestion Queue */}
        <QueuePanel
          title="Ingestion Jobs"
          items={[
            { name: "COG Batch #44 Upload", status: "Running", eta: "3m" },
            { name: "Permian Mosaic Index", status: "Queued", eta: "—" },
          ]}
        />

        {/* Model Processing Queue */}
        <QueuePanel
          title="Model Processing"
          items={[
            { name: "Tank Detection – AOI #19", status: "Running", eta: "7m" },
            { name: "Well Finder – AOI #34", status: "Idle", eta: "—" },
          ]}
        />

      </div>

      {/* CONTROL ACTIONS */}
      <SectionTitle title="Controls & Actions" />
      <div className="flex gap-4">
        <ActionButton
          icon={<UploadCloud size={18} />}
          label="Ingest Imagery"
        />
        <ActionButton
          icon={<RefreshCcw size={18} />}
          label="Trigger Reprocessing"
        />
        <ActionButton
          icon={<PlayCircle size={18} />}
          label="Run AI Model Test"
        />
        <ActionButton
          icon={<Settings size={18} />}
          label="System Settings"
        />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------
   REUSABLE COMPONENTS
--------------------------------------------------------------------*/

function SectionTitle({ title }) {
  return (
    <div className="text-xs font-mono uppercase tracking-[0.14em] text-fg-secondary mb-2 mt-8">
      {title}
    </div>
  );
}

function MetricCard({ icon, title, value, sub }) {
  return (
    <div className="p-4 rounded-lg border border-slate-700 bg-bg-soft shadow-inner shadow-black/30">
      <div className="flex items-center justify-between">
        <div className="text-xs text-gray-400 font-mono uppercase tracking-wide">{title}</div>
        {icon}
      </div>
      <div className="text-xl font-semibold mt-2">{value}</div>
      <div className="text-xs text-fg-secondary mt-1">{sub}</div>
    </div>
  );
}

function UseCaseCard({ icon, title, status, lastRun, alerts }) {
  return (
    <div className="p-4 rounded-lg border border-slate-700 bg-bg-soft hover:border-accent-info/60 transition shadow-inner shadow-black/30">
      <div className="flex items-center justify-between mb-2">
        <div className="font-medium text-sm">{title}</div>
        {icon}
      </div>

      <div className="text-xs text-fg-secondary mb-1">Status: <span className="text-gray-300">{status}</span></div>
      <div className="text-xs text-fg-secondary mb-1">Last Run: <span className="text-gray-300">{lastRun}</span></div>
      <div className="text-xs text-fg-secondary">Alerts: <span className="text-gray-300">{alerts}</span></div>
    </div>
  );
}

function QueuePanel({ title, items }) {
  return (
    <div className="p-4 rounded-lg border border-slate-700 bg-bg-soft shadow-inner shadow-black/30">
      <div className="font-medium text-sm mb-3">{title}</div>

      <div className="space-y-2">
        {items.map((job, i) => (
          <div key={i} className="flex justify-between text-xs bg-bg p-2 rounded-md border border-slate-700">
            <div className="text-gray-300">{job.name}</div>
            <div className="text-gray-400">{job.status}</div>
            <div className="text-fg-secondary">{job.eta}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ActionButton({ icon, label }) {
  return (
    <button className="flex items-center gap-2 px-4 py-2 rounded-md bg-bg-soft border border-slate-700 hover:border-accent-action/60 hover:text-accent-action transition text-sm">
      {icon}
      {label}
    </button>
  );
}

